export interface InstagramMenuDraft {
  id: string; connectionId: string; mediaId: string; title: string | null; category: string | null;
  revision: number; updatedAt: string;
}
export interface InstagramMenuPage<T extends { id: string }> { items: T[]; nextCursor: string | null; connectionId?: string }
export interface InstagramMenuMedia {
  id: string; caption: string | null; mediaType: string; mediaUrl: string | null; thumbnailUrl: string | null;
  permalink: string | null; showOnStorefront: boolean; showOnMarketplace: boolean;
}
const invalid = (): never => { throw new Error('Invalid Instagram menu response'); };
const object = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : invalid();
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value);
const nullable = (value: unknown): value is string | null => value === null || typeof value === 'string';
const text = (value: unknown, limit: number): value is string | null => value === null || typeof value === 'string' && !!value.trim()
  && [...value].length <= limit && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)
  && ![...value].some(char => char.length === 1 && char.charCodeAt(0) >= 0xd800 && char.charCodeAt(0) <= 0xdfff);
export function readInstagramMenuDraft(value: unknown): InstagramMenuDraft {
  const row = object(value);
  if (!uuid(row.id) || !uuid(row.connectionId) || !uuid(row.mediaId) || !text(row.title, 200) || !text(row.category, 100)
    || typeof row.revision !== 'number' || !Number.isInteger(row.revision) || row.revision < 0 || row.revision > 2147483647
    || typeof row.updatedAt !== 'string' || !Number.isFinite(Date.parse(row.updatedAt)) || new Date(row.updatedAt).toISOString() !== row.updatedAt) return invalid();
  return { id: row.id, connectionId: row.connectionId, mediaId: row.mediaId, title: row.title,
    category: row.category, revision: row.revision, updatedAt: row.updatedAt };
}
function page(value: unknown) {
  const row = object(value);
  if (!Array.isArray(row.items) || row.items.length > 50 || !(row.nextCursor === null || typeof row.nextCursor === 'string' && row.nextCursor.length > 0 && row.nextCursor.length <= 4096)) return invalid();
  return { row, items: row.items as unknown[], nextCursor: row.nextCursor };
}
export function readInstagramMenuDraftPage(value: unknown): InstagramMenuPage<InstagramMenuDraft> & { connectionId: string } {
  const { row, items, nextCursor } = page(value);
  if (!uuid(row.connectionId) || nextCursor !== null && !uuid(nextCursor)) return invalid();
  const parsed = items.map(readInstagramMenuDraft);
  if (parsed.some(item => item.connectionId !== row.connectionId) || new Set(parsed.map(item => item.id)).size !== parsed.length) return invalid();
  return { connectionId: row.connectionId, items: parsed, nextCursor };
}
export function readInstagramMenuMediaPage(value: unknown): InstagramMenuPage<InstagramMenuMedia> {
  const { items, nextCursor } = page(value);
  return { nextCursor, items: items.map(value => {
    const row = object(value);
    if (!uuid(row.id) || !nullable(row.caption) || !nullable(row.mediaUrl) || !nullable(row.thumbnailUrl) || !nullable(row.permalink)
      || typeof row.mediaType !== 'string' || !['IMAGE', 'VIDEO', 'CAROUSEL_ALBUM'].includes(row.mediaType)
      || typeof row.showOnStorefront !== 'boolean' || typeof row.showOnMarketplace !== 'boolean') return invalid();
    return { id: row.id, caption: row.caption, mediaType: row.mediaType, mediaUrl: row.mediaUrl, thumbnailUrl: row.thumbnailUrl,
      permalink: row.permalink, showOnStorefront: row.showOnStorefront, showOnMarketplace: row.showOnMarketplace };
  }) };
}
